import { DialogTitle } from '@mui/material';
import { AddItemsTab, AssortmentItemMinimal, AssortmentTag } from '@retail/my-assortment/types';
import { Dialog, Stepper } from '@shared/components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MgSupplierItemSearchResult, ProductGroupBase } from '@retail/products/types';
import { useAppTFunction } from '@retail/app/i18n';
import { AssortmentTagItemsUpdate, useAssortmentValidation } from '@retail/my-assortment/context';
import { withDialogSuspenseErrorBoundary } from '@retail/components';
import { useFetchAssortmentTags } from '@retail/my-assortment/data-access';
import { AddItemsDialogContent } from './AddItemsDialogContent';
import { HandleItems, ItemBatch } from './HandleItems';
import { HandleItemTags } from './HandleItemTags';
import { SelectCustomerAssortments } from './SelectCustomerAssortments';
import { useAddItemsToAssortment } from './useAddItemsToAssortment';
import { useImportAssortmentItemsStore } from '@retail/my-assortment/stores';
import { useFilters } from '@retail/products/hooks';
import { useSearchParams } from 'react-router';
import { BulkActionsFilters, itemBulkActions, searchFilters } from '@retail/products/utils';
import { useCustomerAssortments, useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { OrgUnitType } from '@retail/mgpris/config-types';

const emptyBatch: ItemBatch = { items: [], rest: [] };

interface PendingAdd {
  batch: ItemBatch;
  tagUpdates: AssortmentTagItemsUpdate[];
}

interface Props {
  open: boolean;
  assortmentId: number;
  onClose: (paramsToMutate?: URLSearchParams) => void;
}

function AddItemsToAssortment({ open, assortmentId, onClose }: Props) {
  const t = useAppTFunction();
  const [searchParams, setSearchParams] = useSearchParams();

  const { resetFilters } = useFilters<BulkActionsFilters>(itemBulkActions);
  const resetAllFilters = useCallback(() => resetFilters(searchFilters), [resetFilters]);

  const { importedAssortmentItems, validItems, setValidationState, clearState } =
    useImportAssortmentItemsStore((state) => state);

  const handleOnClose = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    resetFilters(searchFilters, params);
    onClose(params);
    setSearchParams(params, { replace: true });
    clearState();
  }, [clearState, onClose, resetFilters, searchParams, setSearchParams]);

  const { data: assortmentTagDtos = [] } = useFetchAssortmentTags({
    assortmentIds: [assortmentId],
  });
  const assortmentTags = useMemo(
    () => assortmentTagDtos.map(AssortmentTag.fromDTO),
    [assortmentTagDtos]
  );
  const includeTagStep = assortmentTags.length > 0;

  const { orgUnitType } = useSelectedOrgUnit();
  const customerAssortments = useCustomerAssortments();
  const includeCustomerAssortmentsStep =
    orgUnitType === OrgUnitType.WHOLESALE_LOCATION && customerAssortments.length > 0;

  const [activeStep, setActiveStep] = useState(0);
  const steps = useMemo(
    () => [
      t('myAssortment.actions.selectItems'),
      t('myAssortment.actions.editItems'),
      ...(includeTagStep ? [t('myAssortment.actions.editTags')] : []),
      ...(includeCustomerAssortmentsStep ? [t('myAssortment.actions.customerAssortments')] : []),
    ],
    [includeCustomerAssortmentsStep, includeTagStep, t]
  );
  const customerAssortmentsStep = includeTagStep ? 3 : 2;

  const [selectedTab, setSelectedTab] = useState<AddItemsTab>('ITEM_SEARCH');

  const [selectedCentralItems, setSelectedCentralItems] = useState<AssortmentItemMinimal[]>([]);
  const [selectedSearchItems, setSelectedSearchItems] = useState<MgSupplierItemSearchResult[]>([]);

  const { loadingValidateItems, failedValidation } = useAssortmentValidation({
    importedItems: importedAssortmentItems ?? [],
    assortmentId,
    setValidationState,
  });

  useEffect(() => {
    resetAllFilters();
    if (selectedTab !== 'IMPORT') clearState();
    if (selectedTab !== 'CENTRAL_ASSORTMENTS') setSelectedCentralItems([]);
    if (selectedTab !== 'ITEM_SEARCH') setSelectedSearchItems([]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]);

  const assortmentItems = useMemo(() => {
    if (selectedTab === 'CENTRAL_ASSORTMENTS') {
      return selectedCentralItems;
    }
    if (selectedTab === 'IMPORT') {
      return validItems ?? [];
    }
    return selectedSearchItems.map((item) => {
      return {
        ...item,
        mgSupplierItemId: item.id,
        priority: { prioritised: false, validFrom: new Date() },
        supplier: { id: item.mgSupplierId, name: item.participantAccountName },
        productGroup: item.bk04Varegruppe
          ? ProductGroupBase.fromDto({
              name: item.bk04Varegruppe.name,
              groupNumber: item.bk04Varegruppe.groupNumber,
            })
          : undefined,
      };
    }) as AssortmentItemMinimal[];
  }, [selectedCentralItems, selectedSearchItems, selectedTab, validItems]);

  const assortmentItemsWithMgItemNumber = useMemo(
    () => assortmentItems.filter((item) => !!item.mgItemNumber),
    [assortmentItems]
  );

  const [items, setItems] = useState<AssortmentItemMinimal[]>([]);
  const [batchToTag, setBatchToTag] = useState<ItemBatch>(emptyBatch);
  const [pendingAdd, setPendingAdd] = useState<PendingAdd>({ batch: emptyBatch, tagUpdates: [] });

  const continueWithRemainingItems = useCallback(
    (rest: AssortmentItemMinimal[]) => {
      resetAllFilters();
      setItems(rest);
      setActiveStep(1);
    },
    [resetAllFilters]
  );

  const { addItems, isSaving, resetItemsPersisted } = useAddItemsToAssortment({
    onAllItemsAdded: handleOnClose,
    onItemsRemaining: continueWithRemainingItems,
  });

  const handleItemsTagged = useCallback(
    (batch: ItemBatch, tagUpdates: AssortmentTagItemsUpdate[] = []) => {
      if (!includeCustomerAssortmentsStep) {
        addItems(batch, tagUpdates);
        return;
      }
      setPendingAdd({ batch, tagUpdates });
      setActiveStep(customerAssortmentsStep);
    },
    [addItems, customerAssortmentsStep, includeCustomerAssortmentsStep]
  );

  const handleItemsEdited = useCallback(
    (batch: ItemBatch) => {
      setItems([...batch.items, ...batch.rest]);
      resetItemsPersisted();

      if (!includeTagStep) {
        handleItemsTagged(batch);
        return;
      }
      setBatchToTag(batch);
      setActiveStep(2);
    },
    [handleItemsTagged, includeTagStep, resetItemsPersisted]
  );

  return (
    <Dialog open={open} onClose={handleOnClose} fullScreen sx={{ p: 4 }}>
      <DialogTitle sx={{ justifyContent: 'space-between' }}>
        {t('myAssortment.actions.addItemsToAssortment')}
        <Stepper steps={steps} activeStep={activeStep} labelOrientation="horizontal" />
      </DialogTitle>

      {activeStep === 0 && (
        <AddItemsDialogContent
          selectedSearchItems={selectedSearchItems}
          selectedCentralItems={selectedCentralItems}
          importedItems={importedAssortmentItems ?? []}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          setSelectedSearchItems={setSelectedSearchItems}
          setSelectedCentralItems={setSelectedCentralItems}
          onClose={handleOnClose}
          onContinue={() => {
            setItems(assortmentItemsWithMgItemNumber);
            setActiveStep(1);
          }}
          loadingValidateItems={loadingValidateItems}
          failedValidation={failedValidation}
        />
      )}
      {activeStep === 1 && (
        <HandleItems
          items={items}
          setItems={setItems}
          itemsWithoutItemNr={assortmentItems.length - assortmentItemsWithMgItemNumber.length}
          continueToTags={includeTagStep}
          continueToCustomerAssortments={includeCustomerAssortmentsStep}
          isSaving={isSaving}
          onContinue={handleItemsEdited}
          onClose={handleOnClose}
          goBack={() => {
            setActiveStep(0);
            resetFilters();
          }}
        />
      )}
      {includeTagStep && activeStep === 2 && (
        <HandleItemTags
          assortmentItems={batchToTag.items}
          assortmentTags={assortmentTags}
          isSaving={isSaving}
          continueToCustomerAssortments={includeCustomerAssortmentsStep}
          onAddItems={(taggedItems, tagUpdates) =>
            handleItemsTagged({ items: taggedItems, rest: batchToTag.rest }, tagUpdates)
          }
          onClose={handleOnClose}
          goBack={() => setActiveStep(1)}
        />
      )}
      {includeCustomerAssortmentsStep && activeStep === customerAssortmentsStep && (
        <SelectCustomerAssortments
          itemsToAddCount={pendingAdd.batch.items.length}
          isSaving={isSaving}
          onAddItems={(customerAssortmentIds) =>
            addItems(pendingAdd.batch, pendingAdd.tagUpdates, customerAssortmentIds)
          }
          onClose={handleOnClose}
          goBack={() => setActiveStep(customerAssortmentsStep - 1)}
        />
      )}
    </Dialog>
  );
}

export default withDialogSuspenseErrorBoundary(AddItemsToAssortment);
